import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { Add, Delete, Edit, CloudUpload } from '@material-ui/icons';
import GetAppIcon from '@material-ui/icons/GetApp';
import Chip from '@material-ui/core/Chip';
import { solicitudDetalleService } from '../../services';

const steps = ['Datos Personales', 'Domicilio', 'Datos Fiscales', 'Declaraciones'];

const initialFormData = {
  id: null,
  datosPrincipales: {
    nombres: '',
    apellidos: '',
    celular: '',
    correoElectronico: '',
    porcentajeParticipacion: ''
  },
  datosPersonales: {
    id: null,
    tipoID: 'DNI',
    idNumero: '',
    fechaNacimiento: '',
    lugarNacimiento: '',
    nacionalidad: 'ARGENTINA',
    paisOrigen: 'ARGENTINA',
    paisResidencia: 'ARGENTINA',
    actividad: '',
    sexo: 'MASCULINO',
    estadoCivil: 'SOLTERO',
    dniFrenteArchivoId: null,
    dniReversoArchivoId: null,
    conyuge: {
      id: null,
      nombres: '',
      apellidos: '',
      tipoID: 'DNI',
      idNumero: '',
      tipoClaveFiscal: 'CUIT',
      claveFiscal: ''
    }
  },
  domicilio: {
    id: null,
    tipo: 'LEGAL',
    calle: '',
    numero: '',
    piso: '',
    depto: '',
    barrio: '',
    ciudad: '',
    provincia: '',
    pais: 'ARGENTINA',
    cp: ''
  },
  datosFiscales: {
    id: null,
    tipo: 'CUIT',
    claveFiscal: '',
    tipoIva: 'CONSUMIDOR_FINAL',
    tipoGanancia: 'NO_INSCRIPTO',
    residenciaFiscal: 'ARGENTINA',
    debeCompletarFiscalExterior: false
  },
  declaraciones: {
    esPep: false,
    motivoPep: '',
    esFATCA: false,
    motivoFatca: '',
    declaraUIF: false,
    motivoUIF: ''
  }
};

const tipoDocumentoOptions = [
  { value: 'DNI', label: 'DNI' },
  { value: 'LC', label: 'Libreta Cívica' },
  { value: 'LE', label: 'Libreta de Enrolamiento' },
  { value: 'PAS', label: 'Pasaporte' },
  { value: 'EXT', label: 'Cédula Extranjera' }
];

const sexoOptions = [
  { value: 'MASCULINO', label: 'Masculino' },
  { value: 'FEMENINO', label: 'Femenino' },
  { value: 'NO_BINARIO', label: 'No Binario' }
];

const estadoCivilOptions = [
  { value: 'SOLTERO', label: 'Soltero' },
  { value: 'CASADO', label: 'Casado' },
  { value: 'DIVORCIADO', label: 'Divorciado' },
  { value: 'VIUDO', label: 'Viudo' },
  { value: 'CONCUBINATO', label: 'Concubinato' },
  { value: 'SEPARADO', label: 'Separado' }
];

const paisesOptions = [
  'ARGENTINA',
  'BRASIL',
  'CHILE',
  'URUGUAY',
  'PARAGUAY',
  'BOLIVIA',
  'PERU',
  'COLOMBIA',
  'VENEZUELA',
  'ECUADOR',
  'MEXICO',
  'ESPAÑA',
  'ITALIA',
  'FRANCIA',
  'ALEMANIA',
  'ESTADOS UNIDOS',
  'CANADA',
  'REINO UNIDO',
  'REPUBLICA DE COREA',
  'OTRO'
];

const tipoFiscalOptions = [
  'CUIT',
  'CUIL',
  'CDI',
  'NIF',
  'NIE',
  'CIF',
  'RUT',
  'RUN',
  'NIT',
  'SAT',
  'RFC',
  'NSS',
  'SSN',
  'TIN',
  'TaxID',
  'CPF',
  'DUI',
  'RTU',
  'Otro'
];

const tipoIvaOptions = [
  { value: 'RESPONSABLE_INSCRIPTO', label: 'Responsable inscripto' },
  { value: 'RESPONSABLE_MONOTRIBUTO', label: 'Responsable monotributo' },
  { value: 'CONSUMIDOR_FINAL', label: 'Consumidor final' },
  { value: 'EXENTO', label: 'Exento' },
  { value: 'NO_CATEGORIZADO', label: 'No categorizado' },
  { value: 'NO_ALCANZADO', label: 'No alcanzado' }
];

const tipoGananciaOptions = [
  { value: 'INSCRIPTO', label: 'Inscripto' },
  { value: 'NO_INSCRIPTO', label: 'No inscripto' },
  { value: 'EXENTO', label: 'Exento' },
  { value: 'RESPONSABLE_MONOTRIBUTO', label: 'Responsable monotributo' }
];

const SeccionCoTitulares = ({ solicitudId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [cotitulares, setCotitulares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [editingCotitularId, setEditingCotitularId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [provincias, setProvincias] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [provinciaSeleccionadaId, setProvinciaSeleccionadaId] = useState(null);
  const [uploadedDniFiles, setUploadedDniFiles] = useState([]);
  const [uploadingDniFiles, setUploadingDniFiles] = useState(false);

  const cargarCotitulares = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      if (!solicitudId) {
        setError('No se encontró el identificador de la solicitud.');
        return;
      }

      const response = await solicitudDetalleService.getCoTitularesIndividuo(solicitudId);

      // El servicio devuelve el JSON parseado directamente
      const lista = Array.isArray(response) ? response : response?.cotitulares || response?.firmantes || [];
      setCotitulares(lista);
      setError('');
    } catch (err) {
      console.error('Error cargando co-titulares:', err);
      setError('Error al cargar los co-titulares.');
    } finally {
      setLoading(false);
    }
  }, [solicitudId]);

  useEffect(() => {
    if (solicitudId) {
      cargarCotitulares();
    }
  }, [solicitudId, cargarCotitulares]);

  // Cargar provincias al montar el componente
  useEffect(() => {
    const cargarProvincias = async () => {
      try {
        const provinciasData = await solicitudDetalleService.getProvincias();
        if (Array.isArray(provinciasData) && provinciasData.length > 0) {
          setProvincias(provinciasData);
        } else {
          setProvincias([]);
        }
      } catch (err) {
        console.error('Error cargando provincias:', err);
        setProvincias([]);
      }
    };
    cargarProvincias();
  }, []);

  // Cargar localidades cuando se selecciona una provincia
  useEffect(() => {
    const cargarLocalidades = async () => {
      if (provinciaSeleccionadaId) {
        try {
          const localidadesData = await solicitudDetalleService.getLocalidades(provinciaSeleccionadaId);
          setLocalidades(localidadesData || []);
        } catch (err) {
          console.error('Error cargando localidades:', err);
          setLocalidades([]);
        }
      } else {
        setLocalidades([]);
      }
    };
    cargarLocalidades();
  }, [provinciaSeleccionadaId]);

  // Inicializar provincia seleccionada cuando se cargan los datos y las provincias están disponibles
  useEffect(() => {
    const inicializarProvincia = async () => {
      if (formData.domicilio.provincia && provincias.length > 0 && !provinciaSeleccionadaId) {
        const provinciaEncontrada = provincias.find(p => p.codigoUIF === formData.domicilio.provincia);
        if (provinciaEncontrada) {
          setProvinciaSeleccionadaId(provinciaEncontrada.id);
          // Cargar las localidades de esa provincia
          try {
            const localidadesData = await solicitudDetalleService.getLocalidades(provinciaEncontrada.id);
            setLocalidades(localidadesData || []);
          } catch (err) {
            console.error('Error cargando localidades:', err);
          }
        }
      }
    };
    inicializarProvincia();
  }, [formData.domicilio.provincia, provincias, provinciaSeleccionadaId]);

  // Función para cargar archivos de DNI existentes
  const cargarArchivoDNI = async (archivoId, side) => {
    if (!archivoId) return;
    try {
      const archivoInfo = await solicitudDetalleService.getArchivo(archivoId);
      if (archivoInfo) {
        let fileUrl = archivoInfo.url || null;
        let size = 0;
        if (archivoInfo.archivoData) {
          const binaryString = atob(archivoInfo.archivoData);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes]);
          fileUrl = URL.createObjectURL(blob);
          size = blob.size || 0;
        }

        setUploadedDniFiles(prev => {
          const updated = prev.filter(f => f.side !== side);
          updated.push({
            id: archivoId,
            name: archivoInfo.nombre || `DNI ${side === 'frente' ? 'Frente' : 'Dorso'}`,
            side: side,
            size: size,
            url: fileUrl
          });
          return updated;
        });
      }
    } catch (err) {
      console.error(`Error cargando archivo DNI ${side}:`, err);
    }
  };

  const handleOpenCrear = () => {
    setFormData(initialFormData);
    setActiveStep(0);
    setEditingCotitularId(null);
    setFormError('');
    setProvinciaSeleccionadaId(null);
    setLocalidades([]);
    setUploadedDniFiles([]);
    setFormOpen(true);
  };

  const normalizarDatos = (cotitular) => {
    if (!cotitular) return initialFormData;

    const datosPrincipales = cotitular.datosPrincipales || {};
    const datosPersonales = cotitular.datosPersonales || {};
    const domicilio = cotitular.domicilio || {};
    const datosFiscales = cotitular.datosFiscales || {};
    const declaraciones = cotitular.declaraciones || {};
    const conyuge = (datosPersonales.conyuge || cotitular.conyuge) || {};

    return {
      id: cotitular.id || null,
      datosPrincipales: {
        nombres: datosPrincipales.nombres ?? cotitular.nombres ?? '',
        apellidos: datosPrincipales.apellidos ?? cotitular.apellidos ?? '',
        celular: datosPrincipales.celular ?? cotitular.celular ?? '',
        correoElectronico: datosPrincipales.correoElectronico ?? cotitular.correoElectronico ?? '',
        porcentajeParticipacion: datosPrincipales.porcentajeParticipacion ?? cotitular.porcentajeParticipacion ?? cotitular.porcentaje ?? ''
      },
      datosPersonales: {
        id: datosPersonales.id ?? null,
        tipoID: datosPersonales.tipoID ?? cotitular.tipoID ?? 'DNI',
        idNumero: datosPersonales.idNumero ?? cotitular.idNumero ?? '',
        fechaNacimiento: datosPersonales.fechaNacimiento ?? cotitular.fechaNacimiento ?? '',
        lugarNacimiento: datosPersonales.lugarNacimiento ?? cotitular.lugarNacimiento ?? '',
        nacionalidad: datosPersonales.nacionalidad ?? cotitular.nacionalidad ?? 'ARGENTINA',
        paisOrigen: datosPersonales.paisOrigen ?? cotitular.paisOrigen ?? 'ARGENTINA',
        paisResidencia: datosPersonales.paisResidencia ?? cotitular.paisResidencia ?? 'ARGENTINA',
        actividad: datosPersonales.actividad ?? cotitular.actividad ?? '',
        sexo: datosPersonales.sexo ?? cotitular.sexo ?? 'MASCULINO',
        estadoCivil: datosPersonales.estadoCivil ?? cotitular.estadoCivil ?? 'SOLTERO',
        dniFrenteArchivoId: datosPersonales.dniFrenteArchivoId ?? cotitular.dniFrenteArchivoId ?? null,
        dniReversoArchivoId: datosPersonales.dniReversoArchivoId ?? cotitular.dniReversoArchivoId ?? null,
        conyuge: {
          id: conyuge.id ?? null,
          nombres: conyuge.nombres ?? '',
          apellidos: conyuge.apellidos ?? '',
          tipoID: conyuge.tipoID ?? 'DNI',
          idNumero: conyuge.idNumero ?? '',
          tipoClaveFiscal: conyuge.tipoClaveFiscal ?? 'CUIT',
          claveFiscal: conyuge.claveFiscal ?? ''
        }
      },
      domicilio: {
        id: domicilio.id ?? null,
        tipo: domicilio.tipo ?? 'LEGAL',
        calle: domicilio.calle ?? '',
        numero: domicilio.numero ?? '',
        piso: domicilio.piso ?? '',
        depto: domicilio.depto ?? '',
        barrio: domicilio.barrio ?? '',
        ciudad: domicilio.ciudad ?? '',
        provincia: domicilio.provincia ?? '',
        pais: domicilio.pais ?? 'ARGENTINA',
        cp: domicilio.cp ?? ''
      },
      datosFiscales: {
        id: datosFiscales.id ?? null,
        tipo: datosFiscales.tipo ?? 'CUIT',
        claveFiscal: datosFiscales.claveFiscal ?? '',
        tipoIva: datosFiscales.tipoIva ?? 'CONSUMIDOR_FINAL',
        tipoGanancia: datosFiscales.tipoGanancia ?? 'NO_INSCRIPTO',
        residenciaFiscal: datosFiscales.residenciaFiscal ?? 'ARGENTINA',
        debeCompletarFiscalExterior:
          datosFiscales.debeCompletarFiscalExterior ??
          cotitular.debeCompletarFiscalExterior ??
          false
      },
      declaraciones: {
        esPep: declaraciones.esPep ?? cotitular.esPep ?? false,
        motivoPep: declaraciones.motivoPep ?? cotitular.motivoPep ?? '',
        esFATCA: declaraciones.esFATCA ?? cotitular.esFATCA ?? false,
        motivoFatca: declaraciones.motivoFatca ?? cotitular.motivoFatca ?? '',
        declaraUIF: declaraciones.declaraUIF ?? cotitular.declaraUIF ?? false,
        motivoUIF: declaraciones.motivoUIF ?? cotitular.motivoUIF ?? ''
      }
    };
  };

  const handleOpenEditar = (cotitular) => {
    const normalizado = normalizarDatos(cotitular);
    setFormData(normalizado);
    setEditingCotitularId(normalizado.id);
    setActiveStep(0);
    setFormError('');
    // Limpiar provincia seleccionada para que se recargue si existe
    setProvinciaSeleccionadaId(null);
    setLocalidades([]);
    // Limpiar y cargar archivos de DNI existentes
    setUploadedDniFiles([]);
    if (normalizado.datosPersonales.dniFrenteArchivoId) {
      cargarArchivoDNI(normalizado.datosPersonales.dniFrenteArchivoId, 'frente');
    }
    if (normalizado.datosPersonales.dniReversoArchivoId) {
      cargarArchivoDNI(normalizado.datosPersonales.dniReversoArchivoId, 'reverso');
    }
    setFormOpen(true);
  };

  const handleCancelForm = () => {
    if (loading) return;
    setFormOpen(false);
    setFormData(initialFormData);
    setEditingCotitularId(null);
    setActiveStep(0);
    setFormError('');
    setSaving(false);
    setProvinciaSeleccionadaId(null);
    setLocalidades([]);
    setUploadedDniFiles([]);
  };

  const handleFieldChange = async (path, value) => {
    setFormData((prev) => {
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < path.length - 1; i += 1) {
        const key = path[i];
        current[key] = { ...current[key] };
        current = current[key];
      }
      current[path[path.length - 1]] = value;
      return newData;
    });
  };

  // Función para subir archivos de DNI
  const handleDniFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const availableSides = [];
    if (!formData.datosPersonales.dniFrenteArchivoId) availableSides.push('frente');
    if (!formData.datosPersonales.dniReversoArchivoId) availableSides.push('reverso');

    if (availableSides.length === 0) {
      setFormError('Ya cargaste el frente y dorso del DNI. Eliminá un archivo para volver a subir.');
      event.target.value = null;
      return;
    }

    const filesToUpload = files.slice(0, availableSides.length);

    setUploadingDniFiles(true);
    try {
      const uploadedResults = [];

      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        const side = availableSides[i];

        const uploadData = new FormData();
        uploadData.append('file', file);

        const response = await solicitudDetalleService.uploadArchivo(uploadData);

        if (response && response.id) {
          uploadedResults.push({
            id: response.id,
            name: file.name,
            side: side,
            size: file.size,
            url: URL.createObjectURL(file)
          });
        }
      }

      setFormData(prev => {
        const newData = { ...prev };
        uploadedResults.forEach(result => {
          if (result.side === 'frente') {
            newData.datosPersonales.dniFrenteArchivoId = result.id;
          } else if (result.side === 'reverso') {
            newData.datosPersonales.dniReversoArchivoId = result.id;
          }
        });
        return newData;
      });

      setUploadedDniFiles(prev => {
        let updated = [...prev];
        uploadedResults.forEach(result => {
          updated = updated.filter(file => file.side !== result.side);
          updated.push(result);
        });
        return updated;
      });

      setFormError('');
    } catch (err) {
      console.error('Error uploading files:', err);
      setFormError('Error al subir los archivos de DNI');
    } finally {
      setUploadingDniFiles(false);
      event.target.value = null;
    }
  };

  // Función para eliminar archivo de DNI
  const removeDniFile = (fileId) => {
    const fileToRemove = uploadedDniFiles.find(file => file.id === fileId);
    setUploadedDniFiles(prev => prev.filter(file => file.id !== fileId));

    if (fileToRemove) {
      setFormData(prev => ({
        ...prev,
        datosPersonales: {
          ...prev.datosPersonales,
          ...(fileToRemove.side === 'frente'
            ? { dniFrenteArchivoId: null }
            : { dniReversoArchivoId: null })
        }
      }));
    }
  };

  const persistCurrentForm = async () => {
    if (!solicitudId) {
      setFormError('No se encontró el identificador de la solicitud.');
      return false;
    }

    let currentId = formData.id ?? editingCotitularId ?? null;
    if (typeof currentId === 'string') {
      const parsed = Number(currentId);
      if (!Number.isNaN(parsed)) {
        currentId = parsed;
      }
    }
    const payload = {
      id: currentId,
      tipo: 'COTITULAR',
      datosPrincipales: {
        ...formData.datosPrincipales,
        nombres: formData.datosPrincipales.nombres ?? '',
        apellidos: formData.datosPrincipales.apellidos ?? '',
        celular: formData.datosPrincipales.celular ?? '',
        correoElectronico: formData.datosPrincipales.correoElectronico ?? '',
        porcentajeParticipacion: formData.datosPrincipales.porcentajeParticipacion ?? ''
      },
      datosPersonales: {
        ...formData.datosPersonales,
        tipoID: formData.datosPersonales.tipoID ?? 'DNI',
        idNumero: formData.datosPersonales.idNumero ?? '',
        fechaNacimiento: formData.datosPersonales.fechaNacimiento ?? '',
        lugarNacimiento: formData.datosPersonales.lugarNacimiento ?? '',
        nacionalidad: formData.datosPersonales.nacionalidad ?? 'ARGENTINA',
        paisOrigen: formData.datosPersonales.paisOrigen ?? 'ARGENTINA',
        paisResidencia: formData.datosPersonales.paisResidencia ?? 'ARGENTINA',
        actividad: formData.datosPersonales.actividad ?? '',
        sexo: formData.datosPersonales.sexo ?? 'MASCULINO',
        estadoCivil: formData.datosPersonales.estadoCivil ?? 'SOLTERO',
        dniFrenteArchivoId: formData.datosPersonales.dniFrenteArchivoId ?? null,
        dniReversoArchivoId: formData.datosPersonales.dniReversoArchivoId ?? null,
        conyuge: formData.datosPersonales.estadoCivil === 'CASADO' ? {
          ...formData.datosPersonales.conyuge
        } : null
      },
      domicilio: {
        ...formData.domicilio,
        tipo: formData.domicilio.tipo ?? 'LEGAL',
        calle: formData.domicilio.calle ?? '',
        numero: formData.domicilio.numero ?? '',
        piso: formData.domicilio.piso ?? '',
        depto: formData.domicilio.depto ?? '',
        barrio: formData.domicilio.barrio ?? '',
        ciudad: formData.domicilio.ciudad ?? '',
        provincia: formData.domicilio.provincia ?? '',
        pais: formData.domicilio.pais ?? 'ARGENTINA',
        cp: formData.domicilio.cp ?? ''
      },
      datosFiscales: {
        ...formData.datosFiscales,
        tipo: formData.datosFiscales.tipo ?? 'CUIT',
        claveFiscal: formData.datosFiscales.claveFiscal ?? '',
        tipoIva: formData.datosFiscales.tipoIva ?? 'CONSUMIDOR_FINAL',
        tipoGanancia: formData.datosFiscales.tipoGanancia ?? 'NO_INSCRIPTO',
        residenciaFiscal: formData.datosFiscales.residenciaFiscal ?? 'ARGENTINA',
        debeCompletarFiscalExterior: formData.datosFiscales.debeCompletarFiscalExterior ?? false
      },
      declaraciones: {
        esPep: formData.declaraciones.esPep,
        motivoPep: formData.declaraciones.motivoPep,
        esFATCA: formData.declaraciones.esFATCA,
        motivoFatca: formData.declaraciones.motivoFatca,
        declaraUIF: formData.declaraciones.declaraUIF,
        motivoUIF: formData.declaraciones.motivoUIF
      },
      nombres: formData.datosPrincipales.nombres,
      apellidos: formData.datosPrincipales.apellidos,
      celular: formData.datosPrincipales.celular,
      correoElectronico: formData.datosPrincipales.correoElectronico,
      tipoID: formData.datosPersonales.tipoID,
      idNumero: formData.datosPersonales.idNumero,
      fechaNacimiento: formData.datosPersonales.fechaNacimiento,
      lugarNacimiento: formData.datosPersonales.lugarNacimiento,
      nacionalidad: formData.datosPersonales.nacionalidad,
      paisOrigen: formData.datosPersonales.paisOrigen,
      paisResidencia: formData.datosPersonales.paisResidencia,
      actividad: formData.datosPersonales.actividad,
      sexo: formData.datosPersonales.sexo,
      estadoCivil: formData.datosPersonales.estadoCivil,
      dniFrenteArchivoId: formData.datosPersonales.dniFrenteArchivoId,
      dniReversoArchivoId: formData.datosPersonales.dniReversoArchivoId,
      esPep: formData.declaraciones.esPep,
      motivoPep: formData.declaraciones.motivoPep,
      esFATCA: formData.declaraciones.esFATCA,
      motivoFatca: formData.declaraciones.motivoFatca,
      declaraUIF: formData.declaraciones.declaraUIF,
      motivoUIF: formData.declaraciones.motivoUIF
    };

    try {
      setSaving(true);
      let response;
      if (currentId) {
        // Usar los métodos de firmantes ya que el endpoint es el mismo
        response = await solicitudDetalleService.updateFirmanteEmpresa(solicitudId, currentId, payload);
      } else {
        response = await solicitudDetalleService.createFirmanteEmpresa(solicitudId, payload);
        // Si es nuevo co-titular, guardar el ID devuelto
        if (response && response.id) {
          setEditingCotitularId(response.id);
          setFormData(prev => ({ ...prev, id: response.id }));
        }
      }

      // El servicio devuelve el JSON parseado directamente
      if (!response) {
        setFormError('Error al guardar el co-titular.');
        return false;
      }

      setFormError('');
      return true;
    } catch (err) {
      console.error('Error guardando co-titular:', err);
      setFormError('Error al guardar el co-titular.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const isStepValid = (stepIndex) => {
    switch (stepIndex) {
      case 0: {
        const { nombres, apellidos, celular, correoElectronico } = formData.datosPrincipales;
        const { tipoID, idNumero, fechaNacimiento, lugarNacimiento, nacionalidad, paisOrigen, paisResidencia, actividad, sexo, estadoCivil } = formData.datosPersonales;
        // Validar datos principales
        if (!nombres || !apellidos || !celular || !correoElectronico) {
          return false;
        }
        // Validar datos personales
        if (!tipoID || !idNumero || !fechaNacimiento || !lugarNacimiento || !nacionalidad || !paisOrigen || !paisResidencia || !actividad || !sexo || !estadoCivil) {
          return false;
        }
        return true;
      }
      case 1: {
        const { calle, numero, ciudad, provincia, pais, cp } = formData.domicilio;
        if (!calle || !numero || !ciudad || !provincia || !pais || !cp) {
          return false;
        }
        break;
      }
      default:
        break;
    }
    return true;
  };

  const validateStep = (stepIndex) => {
    if (!isStepValid(stepIndex)) {
      switch (stepIndex) {
        case 0: {
          const { nombres, apellidos, celular, correoElectronico } = formData.datosPrincipales;
          const { tipoID, idNumero, fechaNacimiento, lugarNacimiento, nacionalidad, paisOrigen, paisResidencia, actividad, sexo, estadoCivil } = formData.datosPersonales;
          if (!nombres || !apellidos || !celular || !correoElectronico) {
            setFormError('Completá al menos nombre, apellido, celular y correo electrónico.');
          } else if (!tipoID || !idNumero || !fechaNacimiento || !lugarNacimiento || !nacionalidad || !paisOrigen || !paisResidencia || !actividad || !sexo || !estadoCivil) {
            setFormError('Completá todos los datos personales requeridos.');
          }
          break;
        }
        case 1: {
          const { calle, numero, ciudad, provincia, pais, cp } = formData.domicilio;
          if (!calle || !numero || !ciudad || !provincia || !pais || !cp) {
            setFormError('Completá todos los datos del domicilio requeridos (calle, número, ciudad, provincia, país y código postal).');
          }
          break;
        }
        default:
          setFormError('Completá los campos requeridos.');
          break;
      }
      return false;
    }
    setFormError('');
    return true;
  };

  const handleNext = () => {
    if (!validateStep(activeStep)) return;
    persistCurrentForm().then((saved) => {
      if (saved) {
        setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
      }
    });
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSave = async () => {
    if (!validateStep(activeStep)) return;

    persistCurrentForm().then(async (saved) => {
      if (saved) {
        setFormOpen(false);
        setFormData(initialFormData);
        setEditingCotitularId(null);
        setActiveStep(0);
        await cargarCotitulares();
      }
    });
  };

  const isCurrentStepValid = isStepValid(activeStep);

  const handleDelete = async (cotitular) => {
    if (!solicitudId || !cotitular?.id) return;
    const confirmar = window.confirm(
      `¿Seguro que querés eliminar al co-titular ${cotitular.datosPrincipales?.nombres || ''} ${cotitular.datosPrincipales?.apellidos || ''}?`
    );
    if (!confirmar) return;

    try {
      setLoading(true);
      // Usar el método de firmantes ya que el endpoint es el mismo
      await solicitudDetalleService.deleteFirmanteEmpresa(solicitudId, cotitular.id);
      // Si llegamos aquí sin excepción, la eliminación fue exitosa
      await cargarCotitulares();
    } catch (err) {
      console.error('Error eliminando co-titular:', err);
      setError('Error al eliminar el co-titular.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Tipo de documento</InputLabel>
                <Select
                  value={formData.datosPersonales.tipoID}
                  onChange={(e) => handleFieldChange(['datosPersonales', 'tipoID'], e.target.value)}
                  label="Tipo de documento"
                >
                  {tipoDocumentoOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Número de documento"
                variant="outlined"
                value={formData.datosPersonales.idNumero}
                onChange={(e) => handleFieldChange(['datosPersonales', 'idNumero'], e.target.value.replace(/[^0-9]/g, ''))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombres"
                variant="outlined"
                value={formData.datosPrincipales.nombres}
                onChange={(e) => handleFieldChange(['datosPrincipales', 'nombres'], e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Apellidos"
                variant="outlined"
                value={formData.datosPrincipales.apellidos}
                onChange={(e) => handleFieldChange(['datosPrincipales', 'apellidos'], e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Celular"
                variant="outlined"
                value={formData.datosPrincipales.celular}
                onChange={(e) => handleFieldChange(['datosPrincipales', 'celular'], e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Correo electrónico"
                variant="outlined"
                value={formData.datosPrincipales.correoElectronico}
                onChange={(e) => handleFieldChange(['datosPrincipales', 'correoElectronico'], e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha de nacimiento"
                type="date"
                variant="outlined"
                value={formData.datosPersonales.fechaNacimiento}
                onChange={(e) => handleFieldChange(['datosPersonales', 'fechaNacimiento'], e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Lugar de nacimiento"
                variant="outlined"
                value={formData.datosPersonales.lugarNacimiento}
                onChange={(e) => handleFieldChange(['datosPersonales', 'lugarNacimiento'], e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Nacionalidad</InputLabel>
                <Select
                  value={formData.datosPersonales.nacionalidad}
                  onChange={(e) => handleFieldChange(['datosPersonales', 'nacionalidad'], e.target.value)}
                  label="Nacionalidad"
                >
                  {paisesOptions.map((pais) => (
                    <MenuItem key={pais} value={pais}>
                      {pais}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>País de origen</InputLabel>
                <Select
                  value={formData.datosPersonales.paisOrigen}
                  onChange={(e) => handleFieldChange(['datosPersonales', 'paisOrigen'], e.target.value)}
                  label="País de origen"
                >
                  {paisesOptions.map((pais) => (
                    <MenuItem key={pais} value={pais}>
                      {pais}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>País de residencia</InputLabel>
                <Select
                  value={formData.datosPersonales.paisResidencia}
                  onChange={(e) => handleFieldChange(['datosPersonales', 'paisResidencia'], e.target.value)}
                  label="País de residencia"
                >
                  {paisesOptions.map((pais) => (
                    <MenuItem key={pais} value={pais}>
                      {pais}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Actividad"
                variant="outlined"
                value={formData.datosPersonales.actividad}
                onChange={(e) => handleFieldChange(['datosPersonales', 'actividad'], e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Sexo</InputLabel>
                <Select
                  value={formData.datosPersonales.sexo}
                  onChange={(e) => handleFieldChange(['datosPersonales', 'sexo'], e.target.value)}
                  label="Sexo"
                >
                  {sexoOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Estado Civil</InputLabel>
                <Select
                  value={formData.datosPersonales.estadoCivil}
                  onChange={(e) => handleFieldChange(['datosPersonales', 'estadoCivil'], e.target.value)}
                  label="Estado Civil"
                >
                  {estadoCivilOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Carga de DNI */}
            <Grid item xs={12}>
              <Typography variant="h6" style={{ marginBottom: '1rem', color: 'var(--light-blue)', marginTop: '1rem' }}>
                Documento de Identidad
              </Typography>
              <Box display="flex" flexDirection="column" alignItems="center" gap="1rem">
                <input
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  id="dni-file-upload"
                  multiple
                  type="file"
                  onChange={handleDniFileUpload}
                  disabled={uploadingDniFiles || (formData.datosPersonales.dniFrenteArchivoId && formData.datosPersonales.dniReversoArchivoId)}
                />
                <label htmlFor="dni-file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={uploadingDniFiles ? <CircularProgress size={20} /> : <CloudUpload />}
                    disabled={uploadingDniFiles || (formData.datosPersonales.dniFrenteArchivoId && formData.datosPersonales.dniReversoArchivoId)}
                    style={{ borderColor: 'var(--main-green)', color: 'var(--main-green)' }}
                  >
                    {uploadingDniFiles ? 'Subiendo...' : 'Subir DNI (Frente y Dorso)'}
                  </Button>
                </label>
                {uploadedDniFiles.length > 0 && (
                  <Grid container spacing={2} style={{ marginTop: '1rem', width: '100%' }}>
                    {uploadedDniFiles.map((file) => (
                      <Grid item xs={12} key={file.id}>
                        <Card style={{ border: '1px solid #e0e0e0', backgroundColor: '#f9fafb' }}>
                          <CardContent>
                            <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
                              <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                                <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                                  DNI {file.side === 'frente' ? 'Frente' : 'Dorso'}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => removeDniFile(file.id)}
                                  style={{ color: '#f44336' }}
                                >
                                  <Delete />
                                </IconButton>
                              </Box>
                              <Box display="flex" flexDirection="column" marginTop={1} width="100%">
                                <Typography variant="body2" style={{ fontWeight: 'bold', wordBreak: 'break-all' }}>
                                  {file.name}
                                </Typography>
                                {file.size ? (
                                  <Typography variant="caption" color="textSecondary">
                                    {Math.round(file.size / 1024)} KB
                                  </Typography>
                                ) : null}
                                {file.url && (
                                  <Box display="flex" gap={1} marginTop={1} flexWrap="wrap">
                                    <Button
                                      variant="text"
                                      color="primary"
                                      onClick={() => window.open(file.url, '_blank')}
                                      size="small"
                                    >
                                      Ver documento
                                    </Button>
                                    <Button
                                      variant="text"
                                      color="primary"
                                      startIcon={<GetAppIcon />}
                                      onClick={() => {
                                        const link = document.createElement('a');
                                        link.href = file.url;
                                        link.download = file.name || `dni-${file.side}.pdf`;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }}
                                      size="small"
                                    >
                                      Descargar
                                    </Button>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
                {!formData.datosPersonales.dniFrenteArchivoId && (
                  <Typography variant="caption" style={{ color: '#f44336' }}>
                    * Debe subir al menos el frente del DNI
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Campos del Cónyuge - Solo mostrar si Estado Civil es CASADO */}
            {formData.datosPersonales.estadoCivil === 'CASADO' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" style={{ marginBottom: '1rem', color: 'var(--light-blue)', marginTop: '1rem' }}>
                    Datos del Cónyuge
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombres"
                    variant="outlined"
                    value={formData.datosPersonales.conyuge.nombres}
                    onChange={(e) => handleFieldChange(['datosPersonales', 'conyuge', 'nombres'], e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Apellidos"
                    variant="outlined"
                    value={formData.datosPersonales.conyuge.apellidos}
                    onChange={(e) => handleFieldChange(['datosPersonales', 'conyuge', 'apellidos'], e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Tipo de documento</InputLabel>
                    <Select
                      value={formData.datosPersonales.conyuge.tipoID}
                      onChange={(e) => handleFieldChange(['datosPersonales', 'conyuge', 'tipoID'], e.target.value)}
                      label="Tipo de documento"
                    >
                      {tipoDocumentoOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Número de documento"
                    variant="outlined"
                    value={formData.datosPersonales.conyuge.idNumero}
                    onChange={(e) => handleFieldChange(['datosPersonales', 'conyuge', 'idNumero'], e.target.value.replace(/[^0-9]/g, ''))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Tipo de clave fiscal</InputLabel>
                    <Select
                      value={formData.datosPersonales.conyuge.tipoClaveFiscal}
                      onChange={(e) => handleFieldChange(['datosPersonales', 'conyuge', 'tipoClaveFiscal'], e.target.value)}
                      label="Tipo de clave fiscal"
                    >
                      {tipoFiscalOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Clave fiscal"
                    variant="outlined"
                    value={formData.datosPersonales.conyuge.claveFiscal}
                    onChange={(e) => handleFieldChange(['datosPersonales', 'conyuge', 'claveFiscal'], e.target.value)}
                  />
                </Grid>
              </>
            )}
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Calle"
                variant="outlined"
                value={formData.domicilio.calle}
                onChange={(e) => handleFieldChange(['domicilio', 'calle'], e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Número"
                variant="outlined"
                value={formData.domicilio.numero}
                onChange={(e) => handleFieldChange(['domicilio', 'numero'], e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Piso"
                variant="outlined"
                value={formData.domicilio.piso}
                onChange={(e) => handleFieldChange(['domicilio', 'piso'], e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Departamento"
                variant="outlined"
                value={formData.domicilio.depto}
                onChange={(e) => handleFieldChange(['domicilio', 'depto'], e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Barrio"
                variant="outlined"
                value={formData.domicilio.barrio}
                onChange={(e) => handleFieldChange(['domicilio', 'barrio'], e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>País</InputLabel>
                <Select
                  value={formData.domicilio.pais}
                  onChange={(e) => handleFieldChange(['domicilio', 'pais'], e.target.value)}
                  label="País"
                >
                  {paisesOptions.map((pais) => (
                    <MenuItem key={pais} value={pais}>
                      {pais}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Provincia</InputLabel>
                <Select
                  value={provinciaSeleccionadaId || ''}
                  onChange={(e) => {
                    const provinciaId = e.target.value;
                    setProvinciaSeleccionadaId(provinciaId);
                    const provincia = provincias.find(p => p.id === provinciaId);
                    if (provincia) {
                      handleFieldChange(['domicilio', 'provincia'], provincia.codigoUIF);
                    }
                    // Limpiar ciudad cuando cambia la provincia
                    handleFieldChange(['domicilio', 'ciudad'], '');
                  }}
                  label="Provincia"
                >
                  {provincias.map((provincia) => (
                    <MenuItem key={provincia.id} value={provincia.id}>
                      {provincia.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Ciudad</InputLabel>
                <Select
                  value={formData.domicilio.ciudad || ''}
                  onChange={(e) => {
                    const localidadCodigoUIF = e.target.value;
                    handleFieldChange(['domicilio', 'ciudad'], localidadCodigoUIF);
                  }}
                  label="Ciudad"
                  disabled={!provinciaSeleccionadaId || localidades.length === 0}
                >
                  {localidades.map((localidad) => (
                    <MenuItem key={localidad.id} value={localidad.codigoUIF}>
                      {localidad.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Código Postal"
                variant="outlined"
                value={formData.domicilio.cp}
                onChange={(e) => handleFieldChange(['domicilio', 'cp'], e.target.value)}
                required
              />
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Tipo identificador fiscal</InputLabel>
                <Select
                  value={formData.datosFiscales.tipo}
                  onChange={(e) => handleFieldChange(['datosFiscales', 'tipo'], e.target.value)}
                  label="Tipo identificador fiscal"
                >
                  {tipoFiscalOptions.map((tipo) => (
                    <MenuItem key={tipo} value={tipo}>
                      {tipo}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Clave fiscal"
                variant="outlined"
                value={formData.datosFiscales.claveFiscal}
                onChange={(e) => handleFieldChange(['datosFiscales', 'claveFiscal'], e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Tipo IVA</InputLabel>
                <Select
                  value={formData.datosFiscales.tipoIva}
                  onChange={(e) => handleFieldChange(['datosFiscales', 'tipoIva'], e.target.value)}
                  label="Tipo IVA"
                >
                  {tipoIvaOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Tipo Ganancias</InputLabel>
                <Select
                  value={formData.datosFiscales.tipoGanancia}
                  onChange={(e) => handleFieldChange(['datosFiscales', 'tipoGanancia'], e.target.value)}
                  label="Tipo Ganancias"
                >
                  {tipoGananciaOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Residencia Fiscal</InputLabel>
                <Select
                  value={formData.datosFiscales.residenciaFiscal}
                  onChange={(e) => handleFieldChange(['datosFiscales', 'residenciaFiscal'], e.target.value)}
                  label="Residencia Fiscal"
                >
                  {paisesOptions.map((pais) => (
                    <MenuItem key={pais} value={pais}>
                      {pais}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );
      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={formData.declaraciones.esPep}
                    onChange={(e) => handleFieldChange(['declaraciones', 'esPep'], e.target.checked)}
                  />
                }
                label="¿Es Persona Expuesta Políticamente (PEP)?"
              />
              {formData.declaraciones.esPep && (
                <TextField
                  fullWidth
                  label="Motivo PEP"
                  variant="outlined"
                  multiline
                  rows={2}
                  value={formData.declaraciones.motivoPep}
                  onChange={(e) => handleFieldChange(['declaraciones', 'motivoPep'], e.target.value)}
                  style={{ marginTop: '1rem' }}
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={formData.declaraciones.esFATCA}
                    onChange={(e) => handleFieldChange(['declaraciones', 'esFATCA'], e.target.checked)}
                  />
                }
                label="¿Es Residente tributario fuera de Argentina (FATCA)?"
              />
              {formData.declaraciones.esFATCA && (
                <TextField
                  fullWidth
                  label="Motivo FATCA"
                  variant="outlined"
                  multiline
                  rows={2}
                  value={formData.declaraciones.motivoFatca}
                  onChange={(e) => handleFieldChange(['declaraciones', 'motivoFatca'], e.target.value)}
                  style={{ marginTop: '1rem' }}
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={formData.declaraciones.declaraUIF}
                    onChange={(e) => handleFieldChange(['declaraciones', 'declaraUIF'], e.target.checked)}
                  />
                }
                label="¿Es Sujeto Obligado a informar ante la UIF?"
              />
              {formData.declaraciones.declaraUIF && (
                <TextField
                  fullWidth
                  label="Motivo UIF"
                  variant="outlined"
                  multiline
                  rows={2}
                  value={formData.declaraciones.motivoUIF}
                  onChange={(e) => handleFieldChange(['declaraciones', 'motivoUIF'], e.target.value)}
                  style={{ marginTop: '1rem' }}
                />
              )}
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Card>
      <CardContent>
        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'flex-start' : 'center'}
          justifyContent="space-between"
          marginBottom="1.5rem"
          gap="1rem"
        >
          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
            Co-titulares
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleOpenCrear}
            style={{ backgroundColor: 'var(--main-green)', color: '#fff', alignSelf: isMobile ? 'stretch' : 'auto' }}
          >
            Agregar Co-titular
          </Button>
        </Box>

        {error && (
          <Box p={2} mb={2} style={{ backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}

        {loading && !formOpen ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : cotitulares.length === 0 && !formOpen ? (
          <Typography variant="body2" color="textSecondary" align="center">
            Aún no registraste co-titulares. Utilizá el botón "Agregar Co-titular" para comenzar.
          </Typography>
        ) : !formOpen && (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombres</TableCell>
                <TableCell>Apellidos</TableCell>
                {!isMobile && <TableCell>Documento</TableCell>}
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cotitulares.map((cotitular) => {
                const nombres = cotitular?.nombres || cotitular?.datosPrincipales?.nombres || '-';
                const apellidos = cotitular?.apellidos || cotitular?.datosPrincipales?.apellidos || '-';
                const documentoTipo = cotitular?.tipoID || cotitular?.datosPersonales?.tipoID || '';
                const documentoNumero = cotitular?.idNumero || cotitular?.datosPersonales?.idNumero || '';
                const documento =
                  documentoTipo && documentoNumero ? `${documentoTipo} ${documentoNumero}` : documentoTipo || documentoNumero || '-';

                return (
                  <TableRow key={cotitular.id || `${nombres}-${apellidos}`}>
                    <TableCell>{nombres}</TableCell>
                    <TableCell>{apellidos}</TableCell>
                    {!isMobile && <TableCell>{documento}</TableCell>}
                    <TableCell align="right">
                      <IconButton onClick={() => handleOpenEditar(cotitular)}>
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(cotitular)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {formOpen && (
          <Card style={{ marginTop: '2rem' }}>
            <CardContent>
              <Box
                display="flex"
                flexDirection={isMobile ? 'column' : 'row'}
                alignItems={isMobile ? 'flex-start' : 'center'}
                justifyContent="space-between"
                marginBottom="1rem"
                gap="0.75rem"
              >
                <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                  {editingCotitularId ? 'Editar Co-titular' : 'Agregar Co-titular'}
                </Typography>
                <Button onClick={handleCancelForm} disabled={loading}>
                  Cancelar
                </Button>
              </Box>
              {!isMobile ? (
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              ) : (
                <Box
                  padding="0.75rem 1rem"
                  borderRadius="8px"
                  border="1px solid #e0e0e0"
                  bgcolor="#fafafa"
                >
                  <Typography variant="subtitle2" style={{ color: '#555' }}>
                    Paso {activeStep + 1} de {steps.length}
                  </Typography>
                  <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: '0.25rem' }}>
                    {steps[activeStep]}
                  </Typography>
                </Box>
              )}
              <Box marginTop="2rem">{renderStepContent(activeStep)}</Box>
              {formError && (
                <Box marginTop="1.5rem">
                  <Typography variant="body2" style={{ color: '#d32f2f' }}>
                    {formError}
                  </Typography>
                </Box>
              )}
              <Box
                marginTop="2rem"
                display="flex"
                justifyContent={isMobile ? 'center' : 'flex-end'}
                gap="1rem"
                flexWrap="wrap"
              >
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={activeStep === 0 || loading || saving}
                >
                  Volver
                </Button>
                {activeStep < steps.length - 1 ? (
                  <Button
                    variant="outlined"
                    onClick={handleNext}
                    disabled={loading || saving || !isCurrentStepValid}
                  >
                    Continuar
                  </Button>
                ) : (
                  <Button
                    onClick={handleSave}
                    style={{ backgroundColor: 'var(--main-green)', color: '#fff' }}
                    disabled={loading || saving || !isCurrentStepValid}
                  >
                    {saving ? <CircularProgress size={20} color="inherit" /> : 'Guardar Co-titular'}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
};

export default SeccionCoTitulares;
